import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import { useWorkspace } from '../../context/workspace-context';
import { useUsers, usePermanentlyDeleteUser } from '../../hooks/use-user';
import { useDepartments } from '../../hooks/use-department';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pagination } from '../../components/ui/pagination';
import { Card, CardContent } from '../../components/ui/card';
import UserDepartmentsCell from '../../components/user-departments-cell';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowUpDown,
  Search,
  UserCog,
  Trophy,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import { getErrorMessage } from '../../helpers/get-error-message';

const PAGE_SIZE = 20;
const TABLE_COLUMN_COUNT = 7;

export default function UserList() {
  const [sortField, setSortField] = useState<'name' | 'email' | 'roles' | null>(
    null,
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [filterDepartmentIds, setFilterDepartmentIds] = useState<number[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();

  const { data: deptData } = useDepartments(
    currentWorkspace?.id ?? 0,
    !!currentWorkspace,
  );
  const departmentOptions = (deptData?.departments ?? []).map((dept) => ({
    value: dept.id,
    label: dept.name,
  }));

  const { data, isLoading, error } = useUsers(
    currentWorkspace?.id,
    currentPage,
    PAGE_SIZE,
    filterRole,
    filterStatus,
    searchTerm,
    filterDepartmentIds,
  );

  const totalPages = data?.totalPages || 0;

  const permanentlyDelete = usePermanentlyDeleteUser();
  const { showToast } = useToast();
  const [openMenuUserId, setOpenMenuUserId] = useState<number | null>(null);
  const [userToDelete, setUserToDelete] = useState<{
    id: number;
    name: string;
    surname: string;
  } | null>(null);

  const handlePermanentlyDelete = async () => {
    if (!userToDelete || !currentWorkspace?.id) return;

    try {
      await permanentlyDelete.mutateAsync({
        userId: userToDelete.id,
        organisationId: currentWorkspace.id,
      });
      showToast('User permanently deleted', 'success');
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to delete user'), 'error');
    } finally {
      setUserToDelete(null);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const sortUsers = (field: 'name' | 'email' | 'roles') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = useMemo(() => {
    const users = data?.users ?? [];
    if (!sortField) return users;

    return [...users].sort((a, b) => {
      const aValue = sortField === 'roles' ? a.roles.name : a[sortField];
      const bValue = sortField === 'roles' ? b.roles.name : b[sortField];

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.users, sortField, sortDirection]);

  const loading = isLoadingWorkspaces || isLoading;

  return (
    <Card className="rounded-lg overflow-hidden">
      <CardContent className="pt-6 px-8 bg-white">
        <h1 className="text-2xl font-bold mb-6 text-primary">Users</h1>
        <div className="w-full">
          <div className="flex justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Input
                placeholder="Search users..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-64"
                disabled={loading}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleSearch}
                disabled={loading}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Select
                value={filterRole}
                onValueChange={(value) => {
                  setFilterRole(value);
                  setCurrentPage(1);
                }}
                disabled={loading}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue>
                    {filterRole === 'all' ? 'All Roles' : filterRole}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Super Admin">Super Admin</SelectItem>
                  <SelectItem value="Employee">Employee</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-[260px]">
                <ReactSelect
                  isMulti
                  options={departmentOptions}
                  value={departmentOptions.filter((option) =>
                    filterDepartmentIds.includes(option.value),
                  )}
                  onChange={(selectedOptions) => {
                    const ids = (selectedOptions ?? []).map(
                      (option) => option.value,
                    );
                    setFilterDepartmentIds(ids);
                    setCurrentPage(1);
                  }}
                  placeholder="All teams / departments"
                  isDisabled={loading}
                />
              </div>
              <Select
                value={filterStatus}
                onValueChange={(value) => {
                  setFilterStatus(value);
                  setCurrentPage(1);
                }}
                disabled={loading}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue>
                    {filterStatus.charAt(0).toUpperCase() +
                      filterStatus.slice(1)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="text-red-500 mb-4 p-4 bg-red-50 rounded">
              Error:{' '}
              {error instanceof Error ? error.message : 'An error occurred'}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">
                  <Button
                    variant="ghost"
                    size="table-header"
                    onClick={() => sortUsers('name')}
                  >
                    Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="table-header"
                    onClick={() => sortUsers('email')}
                  >
                    Email
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="table-header"
                    onClick={() => sortUsers('roles')}
                  >
                    Role
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>Departments</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_COLUMN_COUNT}
                    className="text-center py-8"
                  >
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : sortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_COLUMN_COUNT}
                    className="text-center py-8 text-gray-500"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {`${user.name} ${user.surname}`}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone ? `+${user.phone}` : ''}</TableCell>
                    <TableCell>{user.roles.name}</TableCell>
                    <TableCell>
                      <UserDepartmentsCell
                        userDepartments={user.user_departments ?? []}
                      />
                    </TableCell>
                    <TableCell>
                      {user.status.charAt(0).toUpperCase() +
                        user.status.slice(1)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/users/${user.id}/stats`)}
                      >
                        <Trophy className="h-4 w-4" />
                      </Button>
                      <Popover
                        open={openMenuUserId === user.id}
                        onOpenChange={(open) =>
                          setOpenMenuUserId(open ? user.id : null)
                        }
                      >
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-52 p-1">
                          <button
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-gray-100"
                            onClick={() => {
                              setOpenMenuUserId(null);
                              navigate(`/users/${user.id}/edit`);
                            }}
                          >
                            <UserCog className="h-4 w-4" />
                            Edit user
                          </button>
                          <button
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setOpenMenuUserId(null);
                              setUserToDelete({
                                id: user.id,
                                name: user.name,
                                surname: user.surname,
                              });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Permanently delete
                          </button>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
          <Dialog
            open={!!userToDelete}
            onOpenChange={(open) => {
              if (!open && !permanentlyDelete.isPending) {
                setUserToDelete(null);
              }
            }}
          >
            <DialogContent className="sm:max-w-[440px]">
              <DialogHeader>
                <DialogTitle>Permanently delete this user?</DialogTitle>
                <DialogDescription>
                  <strong>
                    {userToDelete?.name} {userToDelete?.surname}
                  </strong>{' '}
                  will be erased from the system: their account, points, course
                  progress, test history and activity. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setUserToDelete(null)}
                  disabled={permanentlyDelete.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handlePermanentlyDelete}
                  disabled={permanentlyDelete.isPending}
                >
                  {permanentlyDelete.isPending
                    ? 'Deleting...'
                    : 'Permanently delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
